"use client";
import React from "react";
import "./TopHeader.scss";
import Link from "next/link";
import { ReactSVG } from "react-svg";

// A single day's schedule entry
type ScheduleEntry = {
  day: number;               // 0 = Sunday … 6 = Saturday
  from: string | null;       // opening time in HH:mm, or null if closed
  to: string | null;         // closing time in HH:mm, or null if closed
  isVacation: boolean;       // if true: closed all day
};

// Your editable schedule matrix
const SCHEDULE: ScheduleEntry[] = [
  { day: 0, from: null, to: null, isVacation: false }, // Sunday
  { day: 1, from: "07:30", to: "17:30", isVacation: false }, // Monday
  { day: 2, from: "07:30", to: "17:30", isVacation: false }, // Tuesday
  { day: 3, from: "07:30", to: "17:30", isVacation: false }, // Wednesday
  { day: 4, from: "07:30", to: "17:30", isVacation: false }, // Thursday
  { day: 5, from: "07:30", to: "17:30", isVacation: false }, // Friday
  { day: 6, from: "08:00", to: "16:00", isVacation: false }, // Saturday
];

// Specific one-off closed dates in Eastern time (format: YYYY-MM-DD)
// Example: ["2025-07-01", "2025-12-25"]
const CLOSED_DATES: string[] = [];

// Annual recurring closed days (format: MM-DD)
// Example: ["01-01", "12-25"]
const ANNUAL_CLOSED: string[] = [
  '01-09'
];

// Get current date/time in Eastern (Toronto/Amherstburg)
function getEasternTimeDate(): Date {
  const now = new Date();
  const asString = now.toLocaleString("en-US", {
    timeZone: "America/Toronto",
  });
  return new Date(asString);
}

// Parse "HH:mm" into a Date on the same day
function parseTimeToDate(base: Date, hhmm: string) {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(base);
  d.setHours(h, m, 0, 0);
  return d;
}

// Format helpers for date comparison in Eastern time
function formatYMD(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatMD(date: Date) {
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${m}-${d}`;
}

function isSpecificClosedDate(date: Date) {
  const ymd = formatYMD(date);
  const md = formatMD(date);
  return CLOSED_DATES.includes(ymd) || ANNUAL_CLOSED.includes(md);
}

// Scan forward from `fromDay` for the next non-vacation, non-closed entry
function findNextOpen(baseDate: Date, fromDay: number) {
  // look ahead up to two weeks to skip specific closed dates
  for (let i = 1; i <= 14; i++) {
    const candidate = new Date(baseDate);
    candidate.setDate(candidate.getDate() + i);
    const d = candidate.getDay();
    const e = SCHEDULE.find((s) => s.day === d)!;
    if (!e.isVacation && e.from && e.to && !isSpecificClosedDate(candidate)) {
      const label = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][d];
      return {
        isOpen: false as const,
        nextOpenLabel: label,
        nextOpenTime: e.from,
      };
    }
  }
  return { isOpen: false as const }; // no open days at all in lookahead window
}

// Determine current open/closed status
function getShopStatus() {
  const now = getEasternTimeDate();
  const today = now.getDay();
  const entry = SCHEDULE.find((s) => s.day === today)!;

  // If this exact calendar date is closed (one-off or annual), treat as closed all day
  if (isSpecificClosedDate(now)) {
    return findNextOpen(now, today);
  }

  // If vacation or fully closed:
  if (entry.isVacation || !entry.from || !entry.to) {
    return findNextOpen(now, today);
  }

  const openAt = parseTimeToDate(now, entry.from);
  const closeAt = parseTimeToDate(now, entry.to);

  // BEFORE today's opening → show "Opens Today"
  if (now < openAt) {
    return {
      isOpen: false as const,
      nextOpenLabel: "Today",
      nextOpenTime: entry.from,
    };
  }

  // DURING open hours
  if (now >= openAt && now < closeAt) {
    return { isOpen: true as const, closingTime: entry.to };
  }

  // AFTER today's closing → find next
  return findNextOpen(now, today);
}

// Format "HH:mm" → "H:MMAM/PM"
function formatTime(timeStr?: string) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${m.toString().padStart(2, "0")}${ampm}`;
}

const TopHeader: React.FC = () => {
  const status = getShopStatus();

  return (
    <div className="top-header-wrapper">
      <div className="section-wrap">

        {/* Left: Open/Closed status */}
        <div className="left">
          {status.isOpen ? (
            <>
              <p className="text-mini-14 weight-600 green">Open Now</p>
              <p className="text-mini-14 weight-600 blue-50">
                Until {formatTime(status.closingTime)}
              </p>
            </>
          ) : (
            <>
              <p className="text-mini-14 weight-600" style={{ color: "#f17979" }}>
                Closed Now
              </p>
              {status.nextOpenLabel && status.nextOpenTime && (
                <p className="text-mini-14 weight-600 blue-50">
                  Opens {status.nextOpenLabel} at {formatTime(status.nextOpenTime)}
                </p>
              )}
            </>
          )}
        </div>

        {/* Middle: Promotion link */}
        <div className="middle">
          <p className="text-mini-14 weight-600 blue-50">
            Check out our Catalog,&nbsp;
            <Link href="/shop/catalogue?sort=discount">SHOP NOW</Link>
          </p>
        </div>

        {/* Right: Social icons */}
        <div className="right">
          <p className="text-mini-14 weight-600 blue-50">Follow Us</p>
          <div className="icons-wrapper">
            <a href="mailto:hello@amherstsupply.com" aria-label="Email">
              <ReactSVG src="/assets/TopHeader/email-ic.svg" width={20} height={20} />
            </a>
            <Link href="https://www.facebook.com/amherstsupply" target="_blank" aria-label="Facebook">
              <ReactSVG src="/assets/TopHeader/facebook-ic.svg" width={20} height={20} />
            </Link>
            <Link href="https://www.instagram.com/amherstsupply.castlebuilding/" target="_blank" aria-label="Instagram">
              <ReactSVG src="/assets/TopHeader/instagram-ic.svg" width={20} height={20} />
            </Link>
            <Link href="https://ca.linkedin.com/company/amherst-supply-ltd" target="_blank" aria-label="LinkedIn">
              <ReactSVG src="/assets/TopHeader/linkedin-ic.svg" width={20} height={20} />
            </Link>
            <Link href="https://www.youtube.com/channel/UCNJjOnXACrM1a4vA4jRa6kw" target="_blank" aria-label="YouTube">
              <ReactSVG src="/assets/TopHeader/youtube-ic.svg" width={20} height={20} />
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default TopHeader;
